import * as React from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Icon,
  cn,
} from "@nexus/ui";
import { AI_EDIT_INSTRUCTION_MAX_LENGTH } from "@nexus/contracts";
import { useTranslation } from "react-i18next";

const COUNTER_VISIBLE_THRESHOLD_RATIO = 0.9;
const COUNTER_VISIBLE_FROM = AI_EDIT_INSTRUCTION_MAX_LENGTH * COUNTER_VISIBLE_THRESHOLD_RATIO;

const PROMPT_TEMPLATE_KEYS = [
  "punchierHooks",
  "strengthenCta",
  "clearerConcept",
  "shorterNotes",
  "youngerAudience",
  "tightenPreferences",
  "moreUrgentCta",
  "cutToEssentials",
] as const;

const PROMPT_TEMPLATE_DEFAULTS: Record<(typeof PROMPT_TEMPLATE_KEYS)[number], string> = {
  punchierHooks: "Make the hooks punchier",
  strengthenCta: "Strengthen the CTA",
  clearerConcept: "Make the concept clearer",
  shorterNotes: "Shorten the notes",
  youngerAudience: "Rewrite for a younger audience",
  tightenPreferences: "Tighten creator preferences",
  moreUrgentCta: "Make the CTA more urgent",
  cutToEssentials: "Cut the brief to the essentials",
};

export interface PersonaAiBriefEditorModalProps {
  open: boolean;
  onClose: () => void;
  videoName: string | null;
  opportunityTitle: string | null;
  count?: number;
  onSubmit: (instruction: string, idempotencyKey: string) => void;
  isSubmitting?: boolean;
  error?: string | null;
}

export function PersonaAiBriefEditorModal({
  open,
  onClose,
  videoName,
  opportunityTitle,
  count = 1,
  onSubmit,
  isSubmitting = false,
  error,
}: PersonaAiBriefEditorModalProps) {
  const { t } = useTranslation();
  const [instruction, setInstruction] = React.useState("");
  const [isLocked, setIsLocked] = React.useState(false);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const submittingRef = React.useRef(false);
  const idempotencyKeyRef = React.useRef(crypto.randomUUID());
  const wasSubmittingRef = React.useRef(false);

  React.useEffect(() => {
    if (open) {
      setInstruction("");
      setIsLocked(false);
      submittingRef.current = false;
      wasSubmittingRef.current = false;
      idempotencyKeyRef.current = crypto.randomUUID();
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
  }, [open]);

  React.useEffect(() => {
    if (wasSubmittingRef.current && !isSubmitting) {
      submittingRef.current = false;
      setIsLocked(false);
    }
    wasSubmittingRef.current = Boolean(isSubmitting);
  }, [isSubmitting]);

  const isBusy = isSubmitting || isLocked;

  function handleSubmit() {
    const trimmed = instruction.trim();
    if (!trimmed || isBusy || submittingRef.current) return;
    submittingRef.current = true;
    setIsLocked(true);
    onSubmit(trimmed, idempotencyKeyRef.current);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }

  const canSubmit = instruction.trim().length > 0 && !isBusy;
  const isBulk = count > 1;

  // Closing must always work: the rewrite runs in the background (task-progress)
  // independently of this modal, so dismissing never loses work. Gating close on
  // `isBusy` trapped the modal until the Amber job finished — same class of bug
  // as AD-1648 on the script AI editor.
  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <DialogContent className="sm:max-w-2xl gap-0 p-0">
        <div className="flex items-center gap-3 border-b border-border/60 px-6 py-5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[var(--ai-fill-chip)]">
            <Icon name="sparkles" size="sm" className="text-[var(--ai-text)]" />
          </div>
          <div className="min-w-0 flex-1">
            <DialogTitle className="text-base font-semibold leading-snug">
              {t("personaOpportunities.scripts.briefs.aiEditor.title", "AI Edit")}
            </DialogTitle>
          </div>
        </div>

        <div className="flex flex-col gap-4 px-6 py-5">
          <DialogDescription className="text-sm text-muted-foreground">
            {isBulk
              ? t(
                  "personaOpportunities.scripts.briefs.aiEditor.descriptionBulk",
                  "Tell Adge how to rewrite the selected briefs. Duration, brand, and hook package stay; wording changes.",
                )
              : t(
                  "personaOpportunities.scripts.briefs.aiEditor.description",
                  "Tell Adge how to rewrite this brief. Duration, brand, and hook package stay; wording changes.",
                )}
          </DialogDescription>

          <div className="rounded-lg border border-border bg-muted/30 px-3 py-2.5">
            <p className="line-clamp-2 text-sm font-medium leading-snug text-foreground">
              {isBulk
                ? t("personaOpportunities.scripts.briefs.aiEditor.selectedCount", {
                    defaultValue_one: "{{count}} brief selected",
                    defaultValue_other: "{{count}} briefs selected",
                    count,
                  })
                : videoName?.trim() ||
                  t("personaOpportunities.scripts.briefs.aiEditor.untitled", "Untitled brief")}
            </p>
            {!isBulk && opportunityTitle ? (
              <p className="mt-1 truncate text-[11px] text-muted-foreground">{opportunityTitle}</p>
            ) : null}
          </div>

          <div className="flex flex-wrap gap-2">
            {PROMPT_TEMPLATE_KEYS.map((key) => {
              const label = t(
                `personaOpportunities.scripts.briefs.aiEditor.chips.${key}`,
                PROMPT_TEMPLATE_DEFAULTS[key],
              );
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setInstruction(label);
                    textareaRef.current?.focus();
                  }}
                  disabled={isBusy}
                  className={cn(
                    "rounded-full border px-3 py-1 text-xs transition-colors disabled:pointer-events-none disabled:opacity-50",
                    instruction === label
                      ? "border-primary/40 bg-primary/5 text-foreground"
                      : "border-border text-muted-foreground hover:border-border/80 hover:bg-muted hover:text-foreground",
                  )}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <textarea
            ref={textareaRef}
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t(
              "personaOpportunities.scripts.briefs.aiEditor.placeholder",
              "e.g. Make the hooks punchier",
            )}
            aria-label={t(
              "personaOpportunities.scripts.briefs.aiEditor.instructionLabel",
              "AI edit instructions",
            )}
            rows={3}
            maxLength={AI_EDIT_INSTRUCTION_MAX_LENGTH}
            disabled={isBusy}
            className={cn(
              "w-full resize-none rounded-lg border bg-transparent px-3 py-2.5 text-sm outline-none transition-[color,box-shadow]",
              "placeholder:text-muted-foreground/50",
              "focus:border-[var(--ai)] focus:ring-[3px] focus:ring-[var(--ai-fill-chip)]",
              "disabled:cursor-not-allowed disabled:opacity-50",
              error ? "border-destructive" : "border-input",
            )}
          />

          {instruction.length >= COUNTER_VISIBLE_FROM ? (
            <p
              role="status"
              aria-live="polite"
              className="-mt-2 self-end text-[11px] tabular-nums text-muted-foreground"
            >
              {t("personaOpportunities.scripts.briefs.aiEditor.characterCount", {
                current: instruction.length,
                max: AI_EDIT_INSTRUCTION_MAX_LENGTH,
                defaultValue: "{{current}} / {{max}}",
              })}
            </p>
          ) : null}

          {error ? (
            <p className="flex items-center gap-1.5 text-xs text-destructive">
              <Icon name="alert-circle" size="xs" />
              {error}
            </p>
          ) : null}
        </div>

        <div className="flex items-center justify-between border-t border-border/60 px-6 py-4">
          <span className="text-xs text-muted-foreground">
            {t(
              "personaOpportunities.scripts.briefs.aiEditor.pressEnterHint",
              "Press Enter to submit, Shift+Enter for a new line",
            )}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              size="sm"
              iconStart={isBusy ? "loader" : "sparkles"}
              onClick={handleSubmit}
              disabled={!canSubmit}
              className={isBusy ? "[&_svg]:animate-spin" : ""}
            >
              {t("personaOpportunities.scripts.briefs.aiEditor.submit", {
                count,
                defaultValue_one: "Rewrite brief",
                defaultValue_other: "Rewrite {{count}} briefs",
              })}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
